import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import { Spinner } from "@/components/shared/spinner";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { isAbortError } from "@/utils/helpers";

export type PaginationRequestParams = {
  [key: string]: unknown;
};

type PaginationRequestDTO = {
  initialRequest: () => Promise<void>;
  fetchRequest: () => Promise<void>;
  hasMoreItems: boolean;
  skipRequest?: boolean;
  initialLoading?: boolean;
  spinner?: ReactNode;
};

export const usePaginationRequest = ({
  initialRequest,
  fetchRequest,
  hasMoreItems,
  skipRequest = false,
  initialLoading: _initialLoading = true,
  spinner = <Spinner />,
}: PaginationRequestDTO) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(_initialLoading);
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "100px",
  });

  const loadMoreItems = useCallback(async () => {
    if (hasMoreItems && !loadingMore && !skipRequest) {
      setLoadingMore(true);
      try {
        await fetchRequest();
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(error);
        }
      } finally {
        setLoadingMore(false);
      }
    }
  }, [fetchRequest, hasMoreItems, loadingMore, skipRequest]);

  useEffect(() => {
    if (isIntersecting && !initialLoading && !loadingMore) {
      void loadMoreItems();
    }
  }, [isIntersecting, initialLoading, loadMoreItems, loadingMore]);

  useEffect(() => {
    if (!initialLoading || skipRequest) {
      return;
    }

    let cancelled = false;
    setLoadingMore(true);
    void initialRequest()
      .catch((error) => {
        if (!isAbortError(error)) {
          console.error(error);
        }
      })
      .finally(() => {
        if (cancelled) {
          return;
        }

        setInitialLoading(false);
        setLoadingMore(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialLoading, initialRequest, skipRequest]);

  const loadingMarkup = hasMoreItems ? (
    <div ref={ref} className="flex h-[30px] items-center justify-center p-4">
      {(initialLoading || loadingMore) && spinner}
    </div>
  ) : initialLoading ? (
    <Spinner className="my-2 flex justify-center" size={24} />
  ) : null;

  const reset = useCallback(() => {
    setInitialLoading(true);
  }, []);

  // TODO: add empty state component

  return {
    loadingMarkup,
    setInitialLoading,
    initialLoading,
    loadingMore,
    hasMoreItems,
    ref,
    loadMoreItems,
    reset,
  };
};
